import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Paper,
  Tabs,
  Tab,
  Badge,
  IconButton,
  Tooltip,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm, useConfirmControl } from '../contexts/ConfirmContext';
import { useUnsavedGuard } from '../contexts/UnsavedChangesContext';
import Reports from './Reports';
import Calendar from './Calendar/Calendar';
import EventAttendance from './EventAttendance';
import NewsManagement from './NewsManagement';
import SettingsManagement from './SettingsManagement';
import CoachAvailability from './CoachAvailability/CoachAvailability';
import TrainingBalance from './TrainingBalance';
import UserProfile from './UserProfile';
import UserProfileDialog from './UserProfileDialog';
import MobileBottomNav from './MobileBottomNav';
import ThemeToggle from './ThemeToggle';
import SosAccessLogReport from './SosAccessLogReport';
import CoachStatistics from './CoachStatistics';
import MyPayments from './CoachPayment/MyPayments';
import CoachPaymentManagement from './CoachPayment/CoachPaymentManagement';
import AdminMessages from './AdminMessages';
import MessageInbox from './MessageInbox';
import { messageService } from '../services/messageService';
import NewsReader from './NewsReader';
import { useResponsive } from '../hooks/useResponsive';
import { usePendingAdminCounts } from '../hooks/usePendingAdminCounts';
import { labelWithPendingBadge } from './PendingCountBadge';
import {
  buildTabs,
  tabIndex,
  TabKey,
  APP_TITLE,
  DEFAULT_SETTINGS_SUB,
  HISTORY_PASS_THROUGH_TIMEOUT_MS,
  DashboardScreen,
  SettingsSubKey,
  TAB_PARAM,
  canonicalSearch,
  dashboardTitle,
  isDashboardPath,
  sameScreen,
  screenFromSearch,
  searchForScreen,
} from './dashboardTabs';
import { isReauthDialogOpen } from '../utils/session';
import { userProfileService } from '../services/userProfileService';
import { missingProfileFields, missingProfileText } from '../utils/profileUtils';
import { MOBILE_SCROLLER_PADDING_BOTTOM, MOBILE_SCROLLER_PADDING_X } from './dashboardLayout';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * A FÜL AZ URL-BEN — Vissza gomb, F5, böngészőfül-cím  [Web UX 3. csomag (A), 2026-10-04]
 * ══════════════════════════════════════════════════════════════════════════════════════════
 *
 * MI VOLT A BAJ (jelentés 1.1): az aktív fül sima `useState(0)` volt (régi `Dashboard.tsx:49`) —
 * F5 után mindig a Naptár jött, a böngésző Vissza gombja ELHAGYTA az alkalmazást, a böngészőfül
 * címe mindig „BK Pingpong" volt.
 *
 * MOST: a fül (és a Beállítások al-füle) a `?ful=<slug>&al=<slug>` paraméterben él (slug-tábla:
 * `dashboardTabs.ts`). Betöltéskor onnan indul, fülváltáskor `pushState` írja (a Vissza az előző
 * fülre visz), a Vissza/Előre (`popstate`) pedig visszaállítja.
 *
 * MIÉRT KÖZVETLENÜL A `window.history` (és nem a react-router `useSearchParams`):
 *   1) a `Dashboard` ma router-hook nélkül renderelhető — a `Dashboard*.test.tsx` készletek router
 *      NÉLKÜL renderelik; a `useSearchParams` mindegyiket eltörné (router-kontextus hiba);
 *   2) a react-router v7 `BrowserRouter` CSAK a `popstate`-et és a saját `navigate`-jeit figyeli,
 *      a kézi `pushState`-et nem — ez itt kívánatos: a SAJÁT írásaink csak a lekérdezés-részt
 *      változtatják, a `pathname`-et soha, így az `App.tsx` útvonal-illesztése (`/*` → `AuthWrapper`
 *      → `Dashboard`) nem változik, a Dashboard nem csatolódik le és újra. Vissza/Előre gombnál, ha
 *      a cél is DASHBOARD-ÚTVONAL (`isDashboardPath`: minden, amit az `App.tsx` `/*`-a rajzol — pl.
 *      `/`, de egy ismeretlen `/valami` is), a router is újrarajzol (a `popstate`-re), de ugyanazt az
 *      elemet adja — a fület a saját `popstate`-figyelőnk állítja.
 *      ⚠️ [1. javító kör, validátor F2] A Vissza gomb viszont MÁS útvonalra is vihet (pl.
 *      `/helyszinek` → „főoldal" → Vissza; `/belepes` → `/`): ott a router LEBONTANÁ a Dashboardot,
 *      és a mentetlen adat kérdés nélkül elveszne (`beforeunload` sem jön, ugyanaz a dokumentum).
 *      Ezt az útvonalat-váltó Vissza-t a lenti `installPopStateInterceptor` fogja el a router ELŐTT
 *      (lásd ott);
 *   3) a router saját `history.state`-jét (`usr`/`key`/`idx`) MEGTARTJUK (a `pushState` a meglévő
 *      állapot-objektumot kapja), így a router későbbi `navigate()`-je (pl. kilépéskor a
 *      `/belepes`) a helyes indexszel folytatja.
 */
type UrlWriteMode = 'push' | 'replace';

function writeDashboardUrl(search: string, mode: UrlWriteMode): void {
  const { pathname, hash } = window.location;
  const url = `${pathname}${search}${hash}`;
  if (mode === 'push') {
    window.history.pushState(window.history.state, '', url);
  } else {
    window.history.replaceState(window.history.state, '', url);
  }
}

/**
 * A `popstate` ELFOGÁSA A ROUTER ELŐTT [Web UX 3. csomag (A), 1. javító kör — validátor F2, 2026-10-04].
 *
 * MI VOLT A BAJ: a Dashboard `popstate`-figyelője a router (react-router 7.8.2 `getUrlBasedHistory`,
 * `window.addEventListener('popstate', handlePop)`) UTÁN kötődött be, és nem is tudta megállítani.
 * Ha a Vissza gomb MÁS útvonalra vitt (a validátor valódi Chrome-ban mérte: `/helyszinek` → főoldal
 * → mentetlen üzenet/pipa → Vissza → `/helyszinek`), a router a `popstate`-re azonnal a másik oldalt
 * rajzolta, a Dashboard lebomlott, és a beírt adat KÉRDÉS NÉLKÜL elveszett; nyitott kapu-kérdés
 * mellett pedig árva kérdés maradt a másik oldal fölött, és a „Mégse" `/helyszinek?ful=…`-t írt.
 *
 * HOGYAN: egyetlen, MODUL-SZINTEN (az `import` pillanatában, tehát a `BrowserRouter` első
 * renderelése ELŐTT) bekötött figyelő. A routeré ELŐTT fut, mert a `window`-on a figyelők a
 * BEKÖTÉS SORRENDJÉBEN futnak, és a miénk az első (a router a `BrowserRouter` `useLayoutEffect`-
 * jében köt be, a modul betöltése után).
 *   ⚠️ [2. javító kör, MÉRVE 2026-10-04] Az 1. javító kör itt azt is állította, hogy a Chrome a
 *   célponton a `capture` figyelőt veszi előre — ez a `window`-ra NEM igaz: HeadlessChrome 140-ben
 *   egy korábban bekötött buborékoló és egy később bekötött `capture` figyelő sorrendje
 *   „buborékoló, capture" (mérés: üres lapon két `popstate`-figyelő a `window`-on — előbb egy sima,
 *   utána egy `capture: true` —, majd `history.back()`; a futási sorrend „sima, capture" volt).
 *   E2E negatív próba (`listener_late_capture`): a
 *   felcsatoláskor, `capture`-rel bekötött figyelő mellett a `flow-url-state.spec.ts` útvonal-váltó
 *   esete PIROS. A védelem tehát KIZÁRÓLAG a bekötési sorrenden múlik: ez a modul nem tölthető be
 *   lustán (`React.lazy`), és fejlesztői gyors-újratöltés után (új modul, új figyelő a router
 *   UTÁN) a kifelé vivő Vissza védelme a következő teljes újratöltésig nem él — élesben ilyen nincs.
 *   A `capture: true` ártalmatlan, maradt.
 * MI A TÉNYLEGES VÉDELEM [2. validátor-kör M-1, 2026-10-04 — a korábbi komment pontatlan volt]: NEM a
 * `stopImmediatePropagation()`, hanem az, hogy a figyelő a router `handlePop`-ja ELŐTT, SZINKRON
 * visszaírja a címet a Dashboardra (`returnUrlToDashboard` → `pushState` a Dashboard-bejegyzés
 * router-állapotával). A react-router 7.8.2 `handlePop`-ja (`getUrlBasedHistory`) az eseményből semmit
 * nem olvas: a `window.location`-t és a `history.state.idx`-et nézi — ezek ekkor már a Dashboardot
 * mutatják (ugyanaz az index, `delta` 0), tehát a router ugyanazt a Dashboard-elemet rajzolja, nem
 * bont. A validátor mutánsa (a `stopImmediatePropagation()` kivéve) minden tesztben zöld maradt, és
 * ugyanezt mértük a valódi Chrome-ban is (E2E negatív próba `stop_off`, 2026-10-04: a
 * `flow-url-state.spec.ts` útvonal-váltó esete nélküle is ZÖLD). A `stopImmediatePropagation()`
 * MÁSODLAGOS védelemként maradt (a router így a köztes állapotot sem látja); ártalmatlan, mert a
 * router belső indexe a visszaírt állapottal egyezik (az útvonal-váltó E2E-esetek vele együtt zöldek).
 * Ha nincs felcsatolt Dashboard, vagy nincs mit védeni, a figyelő semmit nem tesz — a router
 * zavartalanul végzi a dolgát (a mai viselkedés).
 */
let activePopStateHandler: ((event: PopStateEvent) => void) | null = null;

function installPopStateInterceptor(): void {
  if (typeof window === 'undefined') return;
  // A modul egyszer fut le (fejlesztői gyors-újratöltésnél újra: akkor a régi modul figyelője a régi,
  // a lecsatoláskor `null`-ra állított változót olvassa, tehát néma marad).
  window.addEventListener(
    'popstate',
    (event: PopStateEvent) => {
      activePopStateHandler?.(event);
    },
    { capture: true }
  );
}
installPopStateInterceptor();

// [Web UX 8 (jelvény-javítás), 2026-10-09] A korábbi VISUALLY_HIDDEN stílus helyett a közös PendingCountBadge.tsx soron belüli
// rejtett szövege adja a „, N teendő vár" nevet (az abszolút rejtett span szóközt tett a vessző elé).

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const confirm = useConfirm();
  const { cancelPending: cancelPendingConfirm } = useConfirmControl();
  const unsavedLabels = useUnsavedGuard();
  const { isMobile } = useResponsive();
  /**
   * A fül-lista szerepkörönként MÁS (a fülek szerepkör szerint épülnek fel), ezért az index nem
   * írható be fix számként — mindenhol a kulcsból számolódik. (A hookok elé került, mert a kezdeti
   * URL-olvasás már a szerepkörrel ellenőriz.)
   */
  const isAdmin = user?.userRole === 'Administrator';
  const isCoach = user?.userRole === 'Coach';
  const tabs = buildTabs(isAdmin, isCoach);
  /**
   * A KEZDETI URL-OLVASÁS — SZÁNDÉKOSAN KAPU NÉLKÜL: betöltéskor (F5, link) még semmilyen képernyő
   * nem jelenthetett be mentetlen adatot, tehát nincs mit védeni. A szerepkörnek nem járó vagy
   * ismeretlen `ful=` → Naptár; a hibás URL-t a lenti igazító effekt `replaceState`-tel javítja.
   * Ez az EGYETLEN hely, ahol a képernyő kapu nélkül áll be; minden későbbi váltás a `commitTab`-on
   * át megy, és annak MINDEN hívója kapuzott (forrás-őr: `Dashboard.unsaved.test.tsx`).
   */
  const [screen, setScreen] = useState<DashboardScreen>(() => screenFromSearch(window.location.search, tabs));
  // Ha a szerepkör közben megváltozna (pl. más felhasználó), a nem járó fül helyett a Naptár.
  const activeKey: TabKey = tabIndex(tabs, screen.tab) >= 0 ? screen.tab : 'calendar';
  const activeSub: SettingsSubKey = screen.sub;
  /** A ténylegesen MUTATOTT képernyő — a `popstate` és a kapu ebből dönt (mindig friss). */
  const screenRef = useRef<DashboardScreen>({ tab: activeKey, sub: activeSub });
  screenRef.current = { tab: activeKey, sub: activeSub };
  /**
   * Nyitva van-e MOST a mentetlen-kapu kérdése. Amíg igen, egy újabb Vissza/Előre NEM nyit második
   * kérdést (a `ConfirmContext` egy új kérdéssel az előzőt „Mégse"-ként zárná le) és nem állít fület:
   * a kapu lezárulásakor igazítjuk az URL-t (lásd `confirmLeaveUnsaved`, `handlePopState`).
   */
  const gateOpenRef = useRef(false);
  /**
   * [1. javító kör, F2] Útvonalat váltó Vissza (NEM Dashboard-útvonal — `isDashboardPath`) a Dashboard
   * elhagyását jelenti. [2. validátor-kör K-1] Korábban „más `pathname`, mint a felcsatoláskori" volt
   * a feltétel: két Dashboard-útvonal közötti Vissza (`/valami` → `/`, mindkettőt a `/*` rajzolja)
   * ezért hamisan „kilépésnek" számított — nem váltott fület, mentetlen adatnál hamis kérdést nyitott.
   *   · `dashLocationRef`: a Dashboard LEGUTÓBBI bejegyzésének útvonala és a router `history.state`-je
   *     (`usr`/`key`/`idx`) — erre állunk vissza, ha a kilépést megállítjuk (a router belső indexe
   *     ilyenkor változatlan marad, mert a `popstate`-jét nem kapta meg, így az állapot egyezik);
   *   · `exitPendingRef`: a kérdés ALATT a böngésző a Dashboardon KÍVÜLRE mutat (a kérdés
   *     „Továbblépés"-e a kilépést hajtja végre, nem fül-váltást);
   *   · `passThroughRef`: a „Továbblépés" utáni saját `history.back()` `popstate`-jét a figyelő
   *     átengedi a routernek (az rajzolja a cél-oldalt). [2. javító kör, 2026-10-04] A jelző NEM
   *     ragadhat be: `HISTORY_PASS_THROUGH_TIMEOUT_MS` után lejár (`passThroughTimerRef`), és csak a
   *     Dashboardról KIVIVŐ `popstate`-et engedi át (lásd `handlePopState` eleje). Enélkül egy el nem
   *     ért `back()` után a KÖVETKEZŐ, mentetlen adat melletti Vissza kérdés nélkül vitte el az adatot
   *     (`Dashboard.urlState.test.tsx` „[beragadt jelző]" esetei, negatív próbával igazolva).
   */
  const dashLocationRef = useRef<{ pathname: string; search: string; state: unknown }>({
    pathname: window.location.pathname,
    search: window.location.search,
    state: window.history.state,
  });
  const exitPendingRef = useRef(false);
  const passThroughRef = useRef(false);
  const passThroughTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | undefined>(undefined);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileDialogUserId, setProfileDialogUserId] = useState<string | undefined>(undefined);
  const [attendanceRefreshTrigger, setAttendanceRefreshTrigger] = useState(0);
  /**
   * Az esemény ablakából a „Jelenlét” gombbal érkező cél-esemény. A jelenléti ív napra szól,
   * ezért a dátum is kell — enélkül a lista a mai napot mutatná, és a kiválasztás elszállna.
   */
  const [attendanceEventId, setAttendanceEventId] = useState<string | undefined>(undefined);
  const [attendanceEventDate, setAttendanceEventDate] = useState<string | undefined>(undefined);
  // [Web regisztráció születési dátum, 2026-10-04] A hiányzó profiladatok MEGNEVEZVE (korábban csak egy
  // igen/nem pötty volt, és az admin is hiányosnak látszott — lásd `utils/profileUtils.ts` fejkomment).
  const [profileMissing, setProfileMissing] = useState<string[]>([]);
  const profileIncomplete = profileMissing.length > 0;
  const profileMissingText = missingProfileText(profileMissing);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  /** A Dashboard-szintű ablakok MOSTANI állapota — a Vissza a kérdés (await) UTÁN is frissen látja. */
  const inboxOpenRef = useRef(inboxOpen);
  inboxOpenRef.current = inboxOpen;
  const profileDialogOpenRef = useRef(profileDialogOpen);
  profileDialogOpenRef.current = profileDialogOpen;

  const checkProfileCompleteness = useCallback(async () => {
    try {
      const profile = await userProfileService.getMyProfile();
      setProfileMissing(missingProfileFields(profile));
    } catch {
      // Silently ignore - badge won't show if profile can't be loaded
    }
  }, []);

  useEffect(() => {
    checkProfileCompleteness();
  }, [checkProfileCompleteness]);

  // Az olvasatlan-szám a felső sáv jelvényéhez. Hiba esetén CSENDBEN marad a régi érték:
  // egy hálózati zökkenő miatt nem villantunk hamis „0 üzenet"-et, és riasztani sem
  // riasztunk a fejlécben — a postafiók megnyitva úgyis megmondja, ha baj van.
  const refreshUnreadMessages = useCallback(async () => {
    try {
      setUnreadMessages(await messageService.getUnreadCount());
    } catch {
      /* szándékosan néma */
    }
  }, []);

  useEffect(() => {
    refreshUnreadMessages();
  }, [refreshUnreadMessages]);

  /**
   * [Web UX 4. csomag (Admin teendők), 2026-10-04] Az admin napi teendői (függő regisztráció / asztalfoglalás / magánóra-igénylés) a
   * Beállítások 11 al-füle között vannak; eddig csak a magánóra-igénylésnek volt jelvénye (és csak az al-fülön). EGY közös forrás
   * (`usePendingAdminCounts`, csak adminnál) táplálja a felső „Beállítások" fület, a mobil alsó sávot ÉS az al-fül jelvényeket
   * (propként a `SettingsManagement`-nek) — a Dashboard alatt nincs második, független lekérés.
   * Frissítés: mountkor; a Beállítások fülre lépéskor és al-fül váltáskor (a `pendingRefreshKey` változásakor); a gyermek-listák
   * betöltése/jóváhagyása után a gyermek `onPendingCountChange`-e. Időzített/fókusz-frissítés NINCS. Hiba esetén a hook néma
   * (az utolsó ismert érték marad). Mountkor, ha a kezdő fül a Beállítások, az al-fül gyermeke maga is lekéri a saját listáját
   * — ez egy ELKERÜLHETETLEN, ártalmatlan duplikált kérés (a sorszám-védelem miatt a későbbi válasz nyer, a szám azonos).
   */
  const pendingAdmin = usePendingAdminCounts(isAdmin);
  const refreshPendingAdmin = pendingAdmin.refresh;
  const pendingRefreshKey = !isAdmin ? null : activeKey === 'settings' ? `settings:${activeSub}` : 'other';
  useEffect(() => {
    if (pendingRefreshKey !== null) refreshPendingAdmin();
  }, [pendingRefreshKey, refreshPendingAdmin]);

  /**
   * Az URL igazítása a MUTATOTT képernyőhöz, `replaceState`-tel (új előzmény-bejegyzés NÉLKÜL):
   * szerepkörnek nem járó / ismeretlen `ful=`, ismeretlen vagy nem-admin `al=`. A tiszta cím
   * (`/`, nincs `ful=`) tiszta marad. Nyitott kapu alatt nem nyúl az URL-hez (ott az URL szándékosan
   * a böngésző-gomb célját mutatja, amíg a felhasználó dönt).
   */
  const normalizeUrl = useCallback(() => {
    if (gateOpenRef.current) return;
    const current = window.location.search;
    const canonical = canonicalSearch(current, screenRef.current);
    if (canonical !== current) writeDashboardUrl(canonical, 'replace');
  }, []);

  useEffect(() => {
    normalizeUrl();
  }, [activeKey, activeSub, normalizeUrl]);

  /** Böngészőfül-cím: `<fül rövid felirata> – BK Pingpong`; a Dashboardon kívül „BK Pingpong". */
  const pageTitle = dashboardTitle(tabs, activeKey);
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);
  useEffect(() => () => {
    document.title = APP_TITLE;
  }, []);

  /**
   * A Vissza/Előre gomb (`popstate`) — a kezelő minden renderben frissül (`popHandlerRef`), a
   * figyelő a modul-szintű, a router ELŐTT futó elfogó (`installPopStateInterceptor`); a
   * felcsatolt Dashboard itt jelentkezik be nála. A kezelő maga lent van (`handlePopState`), a kapuval.
   */
  const popHandlerRef = useRef<(event: PopStateEvent) => void>(() => {});
  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      popHandlerRef.current(event);
    };
    activePopStateHandler = onPopState;
    return () => {
      if (activePopStateHandler === onPopState) activePopStateHandler = null;
      if (passThroughTimerRef.current !== null) clearTimeout(passThroughTimerRef.current);
    };
  }, []);

  const idx = (key: TabKey) => tabIndex(tabs, key);
  const activeTab = idx(activeKey);
  const attendanceTabIndex = idx('attendance');
  const profileTabIndex = idx('profile');

  /** Kézi fülváltásnál a cél-esemény elfelejtendő, különben később visszaugrana rá. */
  const clearAttendanceTarget = () => {
    setAttendanceEventId(undefined);
    setAttendanceEventDate(undefined);
  };

  /**
   * MENTETLEN-VÁLTOZÁS VÉDELEM [2026-09-13]: a fül-váltás nem útvonal-váltás, hanem sima
   * állapot-változás — a React egyszerűen lebontja az addigi képernyőt, és a beírt, még nem mentett
   * adat NYOMTALANUL eltűnik (a jelenléti íven ez 15+ játékos pipája és a beszedett pénz). Ezért a
   * Dashboard MINDEN fül-váltó útja (asztali fülsor, mobil alsó sáv, fejléc-cím) és a kijelentkezés
   * is ezen a kapun megy át. Provider nélkül a guard üres listát ad → a navigáció nem akad meg.
   *
   * `opener` — a kérdést nyitó művelet SZÁNDÉKA [2. validátor-kör F-A, 2026-10-04]:
   *   · `'switch'` (alap): Dashboardon BELÜLI váltás (fül, al-fül, Vissza, naptár → jelenlét);
   *   · `'leave'`: a művelet MAGA hagyja el a Dashboardot (kijelentkezés). Ha a kérdés alatt egy
   *     útvonalat váltó Vissza érkezik, a „Továbblépés" NEM a böngésző célját hajtja végre, hanem a
   *     nyitó műveletet (`true`-t ad, a hívó kijelentkeztet). MÉRVE a hiba (validátor, valódi Chrome):
   *     mentetlen adat → „Kijelentkezés" → kérdés → Vissza a `/helyszinek`-re → „Továbblépés": a
   *     kilépés-ág `false`-t adott, a `logout()` NEM futott, a felhasználó BEJELENTKEZVE maradt (a
   *     `localStorage` token megvolt) — egy közös gépen ez biztonsági hiba. A kijelentkezés szándéka
   *     az erősebb: a felhasználó kifejezetten ki akart lépni, a Vissza ezt nem írhatja felül.
   *     „Mégse" → bejelentkezve marad, a Dashboardon (mint eddig).
   */
  type GateOpener = 'switch' | 'leave';
  const confirmLeaveUnsaved = async (opener: GateOpener = 'switch'): Promise<boolean> => {
    const labels = unsavedLabels();
    if (labels.length === 0) return true;
    gateOpenRef.current = true;
    let confirmed = false;
    try {
      confirmed = await confirm({
        title: 'Mentetlen változás',
        message: `Mentetlen változásod van itt: ${labels.join(', ')}. Ha most továbbmész, a beírt adatok elvesznek.`,
        confirmLabel: 'Továbblépés',
        cancelLabel: 'Mégse',
        // A megerősítés ELDOBJA a beírt adatot, ezért vörös — a `formDirty.discardChangesConfirm`
        // kanonikus döntésével egyezően (koordinátor, 2026-09-13).
        destructive: true,
      });
    } finally {
      gateOpenRef.current = false;
    }
    // [1. javító kör, F2] Ha a kérdés ALATT a böngésző a Dashboardon KÍVÜLRE lépett (útvonalat váltó
    // Vissza — a figyelő megállította és a Dashboard címére állt vissza), akkor a kérdés valójában a
    // KILÉPÉSRŐL szól: „Továbblépés" → oda megyünk, ahová a böngésző végül mutatott (ha a kérdést
    // fül-kattintás vagy Vissza nyitotta); „Mégse" → maradunk. A hívó egyik esetben sem vált fület.
    // [2. validátor-kör F-A] KIVÉVE, ha a kérdést egy MAGA IS kilépő művelet (`'leave'`, kijelentkezés)
    // nyitotta: akkor a „Továbblépés" azt hajtja végre (`true` → a hívó kijelentkeztet); a böngésző
    // célja elmarad — a cím már a Dashboardé (`returnUrlToDashboard`), onnan visz a kijelentkezés a
    // `/belepes`-re (`AuthWrapper`, `replace`).
    const exit = exitPendingRef.current;
    exitPendingRef.current = false;
    if (exit) {
      if (confirmed && opener === 'leave') return true;
      if (confirmed) leaveDashboardViaHistory();
      else restoreUrlToScreen();
      return false;
    }
    // [Web UX 3. csomag (A), 2026-10-04] „Mégse" → a képernyő marad; ha közben a Vissza/Előre
    // gomb az URL-t már elmozdította (a `popstate` idején az URL MÁR az új címet mutatja), az URL-t
    // visszaigazítjuk — különben az URL és a képernyő szétválna (F5 a másik fülre vinne).
    if (!confirmed) restoreUrlToScreen();
    return confirmed;
  };

  /**
   * [1. javító kör, F2] A megállított, útvonalat váltó Vissza utáni állapot: a böngésző a cél-bejegyzésen
   * állt, mi FÖLÉ tettünk egy új bejegyzést a Dashboard címével (`returnUrlToDashboard`). Ezért a cél
   * MINDIG pontosan EGY lépéssel hátrébb van — akár Vissza, akár Előre, akár több lépéses ugrás volt.
   * A `history.back()` `popstate`-jét a figyelő átengedi a routernek (`passThroughRef`), az rajzolja a
   * cél-oldalt; az előzmény ugyanolyan marad, mintha a Vissza elsőre átment volna (Előre → Dashboard).
   * (A `pushState` + mesterséges `popstate` is megoldás lett volna, de az a cél-oldal FÖLÉ egy második
   * bejegyzést tenne, és a következő Vissza a Dashboardra hozná vissza — kusza előzmény.)
   */
  const leaveDashboardViaHistory = () => {
    passThroughRef.current = true;
    // [2. javító kör] Ha a `back()` `popstate`-je nem érkezik meg, a jelző lejár (lásd `passThroughRef`).
    if (passThroughTimerRef.current !== null) clearTimeout(passThroughTimerRef.current);
    passThroughTimerRef.current = setTimeout(() => {
      passThroughRef.current = false;
      passThroughTimerRef.current = null;
    }, HISTORY_PASS_THROUGH_TIMEOUT_MS);
    window.history.back();
  };

  /**
   * [1. javító kör, F2] Megállított, útvonalat váltó Vissza után a cím VISSZA a Dashboardra, a MUTATOTT
   * képernyővel és a Dashboard-bejegyzés router-állapotával (`pushState`, a böngésző-gomb célja fölé —
   * így a cél egy lépéssel hátrébb marad, lásd `leaveDashboardViaHistory`). A paraméter nélküli `/`
   * Naptár-címe tiszta marad (nem kap `?ful=naptar`-t).
   */
  const returnUrlToDashboard = () => {
    const shown = screenRef.current;
    const { pathname, search: lastSearch, state } = dashLocationRef.current;
    // Az idegen paraméterek (csak a `ful`/`al` a miénk) megmaradnak; ha a Dashboard címe paraméter
    // nélküli volt és a Naptár látszik, tiszta marad.
    const search = shown.tab === 'calendar' && !new URLSearchParams(lastSearch).has(TAB_PARAM)
      ? canonicalSearch(lastSearch, shown)
      : searchForScreen(lastSearch, shown);
    window.history.pushState(state, '', `${pathname}${search}`);
  };

  /**
   * Az URL visszaállítása a MUTATOTT képernyőre (a kapu „Mégse"-je és a nyitott újra-belépő ablak
   * alatti Vissza után). `pushState`-tel, nem `replaceState`-tel: a Vissza gomb az ELŐZŐ bejegyzésre
   * lépett — ha azt felülírnánk, a felhasználó elveszítené az előző fület az előzményből, és a
   * következő Vissza már az alkalmazásból vinné ki. Így a következő Vissza újra ugyanoda kérdez.
   * (Előre gomb utáni „Mégse"-nél emiatt az előzmény egy bejegyzéssel hosszabb lesz — elfogadott:
   * a cím és a képernyő egyezik, adat nem vész el.)
   */
  const restoreUrlToScreen = () => {
    const shown = screenRef.current;
    if (sameScreen(screenFromSearch(window.location.search, tabs), shown)) {
      normalizeUrl();
      return;
    }
    writeDashboardUrl(searchForScreen(window.location.search, shown), 'push');
  };

  /**
   * A képernyő-váltás EGYETLEN végrehajtója (a `setScreen` csak itt hívódik). Takarítás: a más
   * felhasználó adatlapja és a naptárból hozott cél-esemény elfelejtendő, különben később
   * visszaugrana rá. `history: 'push'` → új előzmény-bejegyzés (kattintás); `'none'` → az URL már a
   * célt mutatja (Vissza/Előre), csak igazítjuk.
   *
   * ⚠️ MINDEN hívójában a mentetlen-kapu (`confirmLeaveUnsaved`) MEGELŐZI a hívást — ezt a
   * `Dashboard.unsaved.test.tsx` forrás-őre ellenőrzi (a `popstate`-ágat is beleértve).
   */
  const commitTab = (next: DashboardScreen, history: 'push' | 'none') => {
    screenRef.current = next;
    setScreen(next);
    if (next.tab !== 'profile') {
      setEditingUserId(undefined);
    }
    clearAttendanceTarget();
    if (history === 'push') {
      writeDashboardUrl(searchForScreen(window.location.search, next), 'push');
    } else {
      normalizeUrl();
    }
  };

  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    const target = tabs[newValue]?.key;
    if (!target || newValue === activeTab) return;
    if (!(await confirmLeaveUnsaved())) return;
    commitTab({ tab: target, sub: DEFAULT_SETTINGS_SUB }, 'push');
  };

  const handleMobileTabChange = async (newValue: number) => {
    const target = tabs[newValue]?.key;
    if (!target || newValue === activeTab) return;
    if (!(await confirmLeaveUnsaved())) return;
    commitTab({ tab: target, sub: DEFAULT_SETTINGS_SUB }, 'push');
  };

  /**
   * A Beállítások al-fül váltása (vezérelt `SettingsManagement`). [Web UX 3. csomag (A)] Eddig
   * kapu nélküli belső állapot volt; most ugyanazon a kapun és URL-íráson megy át, mint a fülek —
   * a Vissza gomb így az előző al-fülre visz, és az al-fül lebontása sem dobhat el csendben adatot.
   */
  const handleSubTabChange = async (key: SettingsSubKey) => {
    if (activeKey === 'settings' && key === activeSub) return;
    if (!(await confirmLeaveUnsaved())) return;
    commitTab({ tab: 'settings', sub: key }, 'push');
  };

  /**
   * A böngésző Vissza/Előre gombja — ÚJ KILÉPÉSI ÚT [Web UX 3. csomag (A), 2026-10-04].
   *
   * A `popstate` idején az URL MÁR az új címet mutatja, a képernyő még a régit. Sorrend:
   *   1) nyitott kapu-kérdés alatt (gyors Vissza–Előre sorozat) NEM nyitunk második kérdést és nem
   *      váltunk: a kérdés lezárulásakor igazodik minden (lásd 4–5.);
   *   2) a lejárt munkamenet újra-belépő ablaka alatt nem navigálunk (a munkamenet halott, a
   *      mentetlen adat megtartása a cél — `AuthContext`): az URL visszaáll;
   *   3) ha az URL ugyanazt a képernyőt jelenti (pl. a cím csak igazítandó), nincs kérdés;
   *   4) egyébként a MENTETLEN-KAPU: nyitott, mentetlen dialógus (pl. `UserProfileDialog`,
   *      esemény-szerkesztő) mellett is — a MUI modális ablak a fül-gombokat letakarja, a böngésző
   *      Vissza gombját viszont nem. „Mégse" → a képernyő marad, az URL visszaáll (`confirmLeaveUnsaved`);
   *   5) „Továbblépés" (vagy tiszta állapot) → AZ AKKORI URL szerinti képernyő jön (ha a kérdés alatt
   *      a felhasználó még tovább lépkedett, oda, ahová a böngésző végül mutat — így a cím és a
   *      képernyő egyezik; ha visszalépett az eredetire, nem történik semmi).
   * Egy más okból nyitva álló megerősítő kérdés (pl. „Biztosan törlöd?") tiszta állapotban
   * „Mégse"-ként zárul: a lebomló képernyő műveletét ne lehessen utólag megerősíteni.
   *
   * [1. javító kör — validátor F2/F3, 2026-10-04]
   *   6) ÚTVONALAT VÁLTÓ Vissza/Előre (a cél NEM Dashboard-útvonal — `isDashboardPath` —, pl.
   *      `/helyszinek`, `/belepes`): a router ezt a Dashboard lebontásával válaszolná meg. Ha VAN mit
   *      védeni (mentetlen adat, nyitott kapu-kérdés, újra-belépő ablak), a figyelő a router ELŐTT fut
   *      (`installPopStateInterceptor`), a címet AZONNAL, szinkron visszaállítja a Dashboardra
   *      (`returnUrlToDashboard` — EZ a tényleges védelem: a router `handlePop`-ja már a Dashboard
   *      címét olvassa; a `stopImmediatePropagation` másodlagos), és ugyanaz a kapu-kérdés jön;
   *      „Továbblépés" → a böngésző által kért oldal (`leaveDashboardViaHistory`), „Mégse" → marad.
   *      Nyitott kapu-kérdés alatt nem nyílik második kérdés, és nem marad árva kérdés a másik oldal
   *      fölött: a nyitott kérdés „Továbblépés"-e ilyenkor a kilépést hajtja végre. Ha NINCS mit
   *      védeni, a router zavartalanul átvált (csak a más okból nyitott kérdés zárul „Mégse"-ként,
   *      mert a `ConfirmProvider` a router fölött él, és a másik oldal fölött maradna).
   *   7) A Vissza által TÉNYLEGESEN végrehajtott képernyőváltáskor a Dashboard-szintű ablakok
   *      (felhasználó-adatlap, postafiók a benne írt üzenettel) BEZÁRULNAK — a Vissza = „el innen"
   *      (koordinátori döntés, F3). Eddig nyitva maradtak a beírt adattal, miközben a kérdés azt
   *      mondta, „elvesznek". „Mégse" után nyitva maradnak, az adattal. (Útvonal-váltáskor a Dashboard
   *      lebomlik, velük együtt.)
   */
  const handlePopState = async (event: PopStateEvent) => {
    if (passThroughRef.current) {
      passThroughRef.current = false;
      if (passThroughTimerRef.current !== null) {
        clearTimeout(passThroughTimerRef.current);
        passThroughTimerRef.current = null;
      }
      // A saját „Továbblépés" utáni `history.back()` — a router rajzolja a cél-oldalt. Csak a
      // Dashboardról KIVIVŐ lépést engedjük át: ha a `popstate` Dashboard-útvonalon áll, az nem a
      // mi `back()`-ünk (az a Dashboardon kívülre visz), tehát rendes kezelést kap (kapuval).
      if (!isDashboardPath(window.location.pathname)) return;
    }
    if (!isDashboardPath(window.location.pathname)) {
      // 6) ÚTVONALAT VÁLTÓ Vissza/Előre.
      const mustProtect = gateOpenRef.current || isReauthDialogOpen() || unsavedLabels().length > 0;
      if (!mustProtect) {
        cancelPendingConfirm();
        return;
      }
      event.stopImmediatePropagation();
      returnUrlToDashboard();
      if (gateOpenRef.current) {
        // A már nyitott kérdés „Továbblépés"-e most a kilépést hajtja végre (`confirmLeaveUnsaved`).
        exitPendingRef.current = true;
        return;
      }
      if (isReauthDialogOpen()) return;
      // A kapu-kérdés „Továbblépés"-e a `confirmLeaveUnsaved`-ban a kilépést hajtja végre, és
      // `false`-t ad (lent nincs fül-váltás) — kivéve, ha a kérdés alatt a böngésző visszalépett a
      // Dashboardra (akkor az a cél, ahogy a 5. pont mondja).
      exitPendingRef.current = true;
    } else {
      // A böngésző Dashboard-útvonalon áll (akár más `pathname`-en, mint eddig — pl. `/valami` → `/`,
      // mindkettőt az `App.tsx` „minden más" útvonala rajzolja, a router nem bont): ez a bejegyzés a Dashboardé (útvonal +
      // router-állapot), innen állunk vissza egy későbbi, megállított kilépésnél.
      dashLocationRef.current = {
        pathname: window.location.pathname,
        search: window.location.search,
        state: window.history.state,
      };
      if (gateOpenRef.current) {
        exitPendingRef.current = false;
        return;
      }
      if (isReauthDialogOpen()) {
        restoreUrlToScreen();
        return;
      }
      if (sameScreen(screenFromSearch(window.location.search, tabs), screenRef.current)) {
        normalizeUrl();
        return;
      }
      if (unsavedLabels().length === 0) cancelPendingConfirm();
    }
    if (!(await confirmLeaveUnsaved())) return;
    const latest = screenFromSearch(window.location.search, tabs);
    if (sameScreen(latest, screenRef.current)) {
      normalizeUrl();
      return;
    }
    commitTab(latest, 'none');
    closeDashboardDialogs();
  };
  popHandlerRef.current = handlePopState;

  /**
   * Az esemény ablakából: vigyen a jelenléti ívre, ARRA az eseményre.
   *
   * ⚠️ Ez is FÜL-VÁLTÁS, tehát a kapun kell átmennie [NB-3, 2026-09-13]: a `setActiveTab`
   * közvetlen hívása megkerülte a mentetlen-változás kérdést. Ma a naptár-ablakból hívódik (ott
   * a dialógusok maguk kérdeznek), de kapu nélkül egy jövőbeli hívási hely CSENDBEN kimaradna a
   * védelemből — ugyanaz a hiba, ami a (most törölt) `handleEditUser`-ben ült.
   */
  const handleOpenAttendance = async (eventId: string, eventDate: string) => {
    if (!(await confirmLeaveUnsaved())) return;
    // [Web UX 3. csomag (A)] Az URL is a Jelenlétre áll (`?ful=jelenlet`, pushState); a cél-esemény
    // SZÁNDÉKOSAN nem kerül az URL-be (F5 után a jelenléti ív a saját alapértelmezésével nyílik).
    // A cél-eseményt a `commitTab` takarítása UTÁN állítjuk (egy renderbe kötegelve ez marad meg).
    commitTab({ tab: 'attendance', sub: DEFAULT_SETTINGS_SUB }, 'push');
    setAttendanceEventId(eventId);
    setAttendanceEventDate(eventDate);
  };

  const handleLogout = async () => {
    // A kijelentkezés is kilépési pont: a mentetlen adat itt is elveszik (SPA-n belüli
    // állapot-váltás, a `beforeunload` nem fut le). `'leave'`: a kérdés alatti, útvonalat váltó Vissza
    // sem nyelheti el a kijelentkezést (lásd `confirmLeaveUnsaved`).
    if (!(await confirmLeaveUnsaved('leave'))) return;
    // [Web UX 3. csomag (A), 2026-10-04] A `ful`/`al` paraméter kilépéskor eltűnik az URL-ből: egy
    // utána, MÁS szerepkörrel belépő ne a régi fülre érkezzen (a szerepkör-ellenőrzés amúgy is
    // Naptárra tenné a nem járó fület, de egy járó, ám idegen fül félrevezető lenne). A munkamenet
    // végén az `AuthWrapper` úgyis a `/belepes`-re visz (`replace`), ez tartalék-védelem arra az
    // esetre, ha az útvonal-logika változna.
    writeDashboardUrl(searchForScreen(window.location.search, null), 'replace');
    logout();
  };

  const handleOpenUserProfile = (userId?: string) => {
    setProfileDialogUserId(userId);
    setProfileDialogOpen(true);
  };

  const handleCloseUserProfile = () => {
    setProfileDialogOpen(false);
    setProfileDialogUserId(undefined);
    // Trigger refresh of EventAttendance to update unread notes badges
    setAttendanceRefreshTrigger(prev => prev + 1);
  };

  /** A postafiók bezárása — a „Bezárás" gomb és a Vissza (lásd `closeDashboardDialogs`) közös útja. */
  const handleCloseInbox = () => {
    setInboxOpen(false);
    // Bezáráskor újraszámoljuk: a felhasználó olvashatott vagy törölhetett odabent.
    refreshUnreadMessages();
  };

  /**
   * [1. javító kör — validátor F3, 2026-10-04] A Vissza által TÉNYLEGESEN végrehajtott képernyőváltás
   * után a Dashboard-szintű ablakok bezárulnak (koordinátori döntés: a Vissza = „el innen"). Mérve
   * (validátor, valódi Chrome): a felhasználó-adatlap és a postafiók (benne az „Új üzenet" írással) a Vissza +
   * „Továbblépés" után NYITVA maradt a beírt adattal, holott a kérdés azt mondta, „elvesznek".
   * A bezárás a szokásos záró utakon megy (`handleCloseUserProfile`, `handleCloseInbox`): mindkét
   * ablak mentetlen-bejelentése `open &&`-tel kapuzott (`UserProfileDialog.tsx` `isDirty`,
   * `ComposeMessageDialog.tsx` `isDirty`; a postafiók zárásakor az írás is zárul —
   * `MessageInbox.tsx`), tehát nem marad ragadt mentetlen-címke; az olvasatlan-szám frissül.
   * A refekből olvasunk: a kérdés (await) alatt a closure-beli állapot elavult lehet.
   */
  const closeDashboardDialogs = () => {
    if (profileDialogOpenRef.current) handleCloseUserProfile();
    if (inboxOpenRef.current) handleCloseInbox();
  };


  if (!user) return null;

  return (
    <Box sx={{
      flexGrow: 1,
      ...(isMobile && {
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        overflow: 'hidden',
      }),
    }}>
      <AppBar position="static" sx={{ flexShrink: 0 }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            component="div"
            sx={{
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              }
            }}
            // A cím kattintása a Naptár fülre navigál, NEM logout — eddig ugyanazt a logout()-ot hívta, mint
            // a Kijelentkezés gomb, holott a title "Vissza a főoldalra"-t ígért. [Web UX #1, 2026-09-12]
            // Ugyanaz a takarítás, mint a fülváltásnál (editingUserId, cél-esemény) — különben a másik
            // felhasználó adatlapja „ragadna" a következő Adatlap-váltásig (validátor B4).
            onClick={() => handleMobileTabChange(idx('calendar'))}
            title="Vissza a főoldalra"
          >
            {isMobile ? 'BK Pingpong' : 'BK Pingpong Eseménykezelő'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mr: 1,
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {user.name} ({user.userRoleDisplayName})
          </Typography>
          <Tooltip title="Üzenetek">
            <IconButton
              color="inherit"
              aria-label="Üzenetek"
              onClick={() => setInboxOpen(true)}
              size={isMobile ? "small" : "medium"}
            >
              <Badge badgeContent={unreadMessages} color="error" max={99}>
                <MailOutlineIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <ThemeToggle size={isMobile ? "small" : "medium"} />
          <Button
            color="inherit"
            onClick={handleLogout}
            size={isMobile ? "small" : "medium"}
            // [Web UX 9 / T2, 2026-10-13] Egységes felirat: korábban telefonon „Kilépés", asztalon
            // „Kijelentkezés" (lelet: `Dashboard.tsx:746`); a mobil app is „Kijelentkezés"-t ír
            // (`mobile/app/(tabs)/profile.tsx:379`). A hosszabb szó telefonon se törjön két sorba, és a
            // gomb ne zsugorodjon — a helyet a (kipontozódó) cím adja át.
            sx={{
              minWidth: { xs: 'auto', sm: '64px' },
              px: { xs: 1, sm: 2 },
              ml: 1,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
            data-testid="logout-button"
          >
            Kijelentkezés
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{
        px: { xs: MOBILE_SCROLLER_PADDING_X, sm: 2, md: 3 },
        mt: isMobile ? 1 : 4,
        mb: isMobile ? 0 : 4,
        ...(isMobile && {
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          // [Web UX 9 / T3] közös konstans: a jelenléti ív sticky mentés-sávja is ehhez igazodik.
          pb: MOBILE_SCROLLER_PADDING_BOTTOM,
        }),
      }}>
        {/* Felső fülsor MINDEN nem-telefon szélességen (≥600 px). Eddig csak ≥900 px-en látszott, az alsó
            sáv pedig csak <600 px-en → 600–899 px között (álló tablet, kis laptop) SEMMILYEN navigáció
            nem volt. [Web UX A1, 2026-09-12] A fülek a közös listából jönnek (dashboardTabs). */}
        {!isMobile && (
          <Paper sx={{ mb: 3 }} data-testid="dashboard-top-tabs">
            {/* allowScrollButtonsMobile: érintős tableten a MUI alapból elrejti a görgető nyilakat — 10 admin-füllel kell. */}
            <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
              {tabs.map((t) => (
                <Tab
                  key={t.key}
                  data-testid={`tab-${t.key}`}
                  // A pötty nem csak szín: a `title` megnevezi, MI hiányzik (egérrel a tooltip, olvasóval az
                  // akadálymentes LEÍRÁS). ⚠️ SZÁNDÉKOSAN NEM `aria-label`: az felülírná a fül nevét, és a
                  // „… születési dátum" szöveg a `getByLabel('Dátum')`-szerű (részszöveges) keresésekbe is
                  // beleütközne — élőben MÉRVE: a `flow-quick-wins-extra` 7) tesztje így strict-mode hibával bukott.
                  {...(t.key === 'profile' && profileIncomplete ? { title: profileMissingText } : {})}
                  label={
                    t.key === 'profile' && profileIncomplete ? (
                      <Badge color="error" variant="dot" sx={{ '& .MuiBadge-badge': { top: 2, right: -8 } }}>
                        {t.label}
                      </Badge>
                    ) : t.key === 'settings' ? (
                      // [Web UX 4. csomag] A szám aria-hidden, a vizuálisan rejtett szöveg („, N teendő vár") teszi az akadálymentes
                      // nevet értelmessé; a fül neve továbbra is tartalmazza a „Beállítások" szót. (Nem aria-label: az felülírná a nevet.)
                      // [Web UX 8 (jelvény-javítás), 2026-10-09] A jelvény a címke UTÁN, a soron belül ül. Régen itt
                      // `right: -16` abszolút eltolás volt: a fül `overflow: hidden`-je levágta a 2. számjegyet (élesben
                      // a „10"-ből csak az „1" látszott). Részletek és mérés: PendingCountBadge.tsx.
                      labelWithPendingBadge(t.label, pendingAdmin.total, 'settings-pending-badge')
                    ) : t.label
                  }
                />
              ))}
            </Tabs>
          </Paper>
        )}

        {activeTab === idx('calendar') && <Calendar onNavigateToUserProfile={handleOpenUserProfile} onNavigateToAttendance={handleOpenAttendance} />}
        {activeTab === profileTabIndex && <UserProfile editUserId={editingUserId} onProfileSaved={checkProfileCompleteness} />}
        {activeTab === idx('stats') && !isAdmin && (isCoach ? <CoachStatistics /> : <TrainingBalance />)}
        {activeTab === idx('availability') && (isAdmin || isCoach) && <CoachAvailability />}
        {activeTab === attendanceTabIndex && (isAdmin || isCoach) && <EventAttendance onNavigateToUserProfile={handleOpenUserProfile} refreshTrigger={attendanceRefreshTrigger}
            initialEventId={attendanceEventId} initialEventDate={attendanceEventDate} />}
        {activeTab === idx('news') && (isAdmin ? <NewsManagement /> : <NewsReader />)}
        {activeTab === idx('reports') && isAdmin && <Reports />}
        {activeTab === idx('settings') && isAdmin && (
          <SettingsManagement
            onEditUser={handleOpenUserProfile}
            activeSubKey={activeSub}
            onSubTabChange={handleSubTabChange}
            pendingCounts={pendingAdmin.counts}
            onPendingCountChange={pendingAdmin.report}
          />
        )}
        {activeTab === idx('sos') && isAdmin && <SosAccessLogReport />}
        {activeTab === idx('coachPayments') && isAdmin && <CoachPaymentManagement />}
        {activeTab === idx('messages') && isAdmin && <AdminMessages />}
        {activeTab === idx('myPayments') && isCoach && <MyPayments />}
      </Box>

      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={handleMobileTabChange}
        tabs={tabs}
        isCoach={isCoach}
        profileIncomplete={profileIncomplete}
        profileMissingText={profileMissingText}
        settingsPendingCount={pendingAdmin.total}
      />

      <MessageInbox
        open={inboxOpen}
        onClose={handleCloseInbox}
        onUnreadChange={refreshUnreadMessages}
      />

      <UserProfileDialog
        open={profileDialogOpen}
        onClose={handleCloseUserProfile}
        userId={profileDialogUserId}
      />
    </Box>
  );
};

export default Dashboard;